import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  ListChecks,
  CalendarDays,
  BookOpen,
  Building2,
  Settings,
  LogOut,
  Sun,
  BarChart3,
  Activity,
  ClipboardCheck,
  ExternalLink,
  FileText,
  ShieldCheck,
  Menu,
  X,
  Calendar,
  Search,
  Trash2,
  BrainCircuit,
  LayoutTemplate,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import PreviewBanner from "@/components/PreviewBanner";
import OpenCheckInReminder from "@/components/OpenCheckInReminder";
import PageErrorBoundary from "@/components/PageErrorBoundary";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["Admin", "Manager", "Executive"] },
  { to: "/my-tasks", label: "My Work", icon: ListChecks, roles: ["Admin", "Manager", "Executive"] },
  { to: "/clients", label: "Clients", icon: Building2, roles: ["Admin", "Manager", "Executive"] },
  { to: "/knowledge", label: "Knowledge", icon: Search, roles: ["Admin", "Manager", "Executive"] },
  { to: "/second-brain", label: "Second Brain", icon: BrainCircuit, roles: ["Admin", "Manager", "Executive"] },
  { to: "/forms", label: "Forms", icon: FileText, roles: ["Admin", "Manager", "Executive"] },
  { to: "/task-templates", label: "Task Templates", icon: LayoutTemplate, roles: ["Admin", "Manager"] },
  { to: "/staff", label: "Staff", icon: Users, roles: ["Admin", "Manager"] },
  { to: "/leave", label: "Leave", icon: CalendarDays, roles: ["Admin", "Manager", "Executive"] },
  { to: "/attendance", label: "Attendance", icon: ClipboardCheck, roles: ["Admin", "Manager"] },
  { to: "/my-attendance", label: "My Attendance", icon: Calendar, roles: ["Admin", "Manager", "Executive"] },
  { to: "/holidays", label: "Holidays", icon: Sun, roles: ["Admin", "Manager", "Executive"] },
  { to: "/resources", label: "Resources", icon: BookOpen, roles: ["Admin", "Manager", "Executive"] },
  { to: "/analytics", label: "Analytics", icon: BarChart3, roles: ["Admin", "Manager"] },
  { to: "/activity", label: "Activity Log", icon: Activity, roles: ["Admin"] },
  { to: "/recently-deleted", label: "Recently Deleted", icon: Trash2, roles: ["Admin", "Manager"] },
  { to: "/roles", label: "Roles & Rights", icon: ShieldCheck, roles: ["Admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["Admin"] },
];

export default function Layout() {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tools, setTools] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/external-tools").then((r) => setTools(r.data || [])).catch(() => setTools([]));
  }, [user]);

  // Auto-close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (!user) return null;

  const visibleNav = navItems.filter((n) => n.roles.includes(user.role));
  const portalName = settings?.portal_name || "Griffin Consultancy Pulse";
  const portalTagline = settings?.portal_tagline || "Internal Portal";
  const [brandPrimary, brandSecondary] = splitBrand(portalName);

  const sidebarContent = (
    <>
      <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between gap-2">
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" data-testid="brand-link">
          <BrandMark logoFileId={settings?.logo_file_id} />
          <div className="leading-tight min-w-0">
            <div className="text-sm font-semibold tracking-tight truncate" style={{ fontFamily: "Outfit" }}>
              {brandPrimary}
            </div>
            <div className="text-[10px] gc-label truncate">{brandSecondary || portalTagline}</div>
          </div>
        </Link>
        <button
          type="button"
          className="md:hidden p-1 rounded-sm hover:bg-white"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          data-testid="sidebar-close-mobile"
        >
          <X size={16} />
        </button>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleNav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                isActive
                  ? "bg-[#0a2540] text-white"
                  : "text-slate-700 hover:bg-white hover:text-[#0a2540]"
              }`
            }
          >
            <Icon size={16} />
            <span>{label}</span>
          </NavLink>
        ))}

        <div className="pt-4 mt-2 border-t border-slate-200">
          <div className="px-3 pb-1.5 gc-label">External Tools</div>
          {tools.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-slate-400 italic">None yet</div>
          )}
          {tools.map((t) => (
            <a
              key={t.id}
              href={t.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2 text-sm rounded-sm text-slate-700 hover:bg-white hover:text-[#0a2540] group"
              data-testid={`external-${(t.short || t.label || "tool").toLowerCase()}`}
            >
              <div className="h-4 w-4 rounded-[2px] border border-slate-300 bg-white inline-flex items-center justify-center text-[8px] font-bold text-slate-500 group-hover:border-[#0a2540] group-hover:text-[#0a2540]">
                {(t.short || (t.label || "?").slice(0, 2)).toUpperCase()}
              </div>
              <span className="flex-1 truncate">{t.label}</span>
              <ExternalLink size={11} className="text-slate-300 group-hover:text-[#0a2540]" />
            </a>
          ))}
        </div>
      </nav>

      <div className="px-3 py-3 border-t border-slate-200">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-3 px-2 py-2 hover:bg-white rounded-sm"
              data-testid="user-menu-trigger"
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <div className="h-8 w-8 bg-[#0a2540] text-white text-sm rounded-full flex items-center justify-center">
                  {user.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="text-left flex-1 min-w-0">
                <div className="text-xs font-semibold truncate" data-testid="user-name">{user.name}</div>
                <div className="text-[10px] gc-label" data-testid="user-role">{user.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-mono-data text-xs">{user.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user.role === "Admin" && (
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" /> Settings
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={logout} data-testid="menu-logout">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-white">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-12 bg-white border-b border-slate-200 flex items-center justify-between px-3" data-testid="mobile-topbar">
        <button
          type="button"
          className="p-1.5 rounded-sm hover:bg-slate-100"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          data-testid="btn-open-sidebar"
        >
          <Menu size={20} />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
          <BrandMark logoFileId={settings?.logo_file_id} size={24} />
          <span className="text-xs font-semibold truncate" style={{ fontFamily: "Outfit" }}>{brandPrimary}</span>
        </Link>
        <div className="w-8" />
      </div>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — fixed off-canvas on mobile, static on md+ */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 w-60 shrink-0 border-r border-slate-200 bg-[#f8f9fa] flex flex-col transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden pt-12 md:pt-0">
        <PreviewBanner />
        <OpenCheckInReminder />
        <PageErrorBoundary routeKey={location.pathname}>
          <Outlet />
        </PageErrorBoundary>
      </main>
    </div>
  );
}

/** Split a portal name like "Griffin Consultancy Pulse" into ("Griffin Consultancy", "Pulse") */
function splitBrand(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (parts.length >= 2) {
    return [parts.slice(0, -1).join(" "), parts.slice(-1)[0].toUpperCase()];
  }
  return [name || "", ""];
}

// Hardcoded portal logo — placed under public/ so it's served from the
// frontend with aggressive caching. Bypasses the (legacy) /api/branding/logo
// flow so the brand is consistent and works on every environment.
const HARDCODED_LOGO_URL = `${process.env.PUBLIC_URL || ""}/branding/griffin-logo.png`;

export function BrandMark({ logoFileId, size = 32 }) {
  return (
    <img
      src={HARDCODED_LOGO_URL}
      alt="Griffin Consultancy"
      style={{ width: size, height: size }}
      className="rounded-sm object-contain bg-white"
      data-testid="brand-logo"
    />
  );
}

export function PageHeader({ title, subtitle, action, children, leading }) {
  const { settings } = useAuth();
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="px-4 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-end gap-3 sm:gap-4 min-w-0 flex-1">
          {leading ? (
            <div className="shrink-0">{leading}</div>
          ) : (
            <div className="shrink-0 hidden md:block" data-testid="page-brandmark">
              <BrandMark logoFileId={settings?.logo_file_id} size={40} />
            </div>
          )}
          <div className="min-w-0">
            {subtitle && <div className="gc-label mb-1">{subtitle}</div>}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }} data-testid="page-title">
              {title}
            </h1>
            {children}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2 sm:gap-3 flex-wrap">
          <NotificationBell />
          {action}
        </div>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const cls = `status-pill status-${(status || "").toLowerCase().replace(/\s+/g, "-")}`;
  return <span className={cls} data-testid={`status-${(status || "").toLowerCase().replace(/\s+/g, "-")}`}>{status}</span>;
}

export function ClientLogo({ fileId, name, size = 40 }) {
  const px = `${size}px`;
  if (fileId) {
    return (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download`}
        alt={name}
        style={{ width: px, height: px }}
        className="rounded-sm border border-slate-200 object-cover bg-white"
        data-testid="client-logo"
      />
    );
  }
  const initial = (name || "?").trim()[0]?.toUpperCase() || "?";
  return (
    <div
      style={{ width: px, height: px }}
      className="rounded-sm border border-slate-200 bg-[#0a2540] text-white flex items-center justify-center font-semibold"
      data-testid="client-logo-placeholder"
    >
      <span style={{ fontFamily: "Outfit", fontSize: size / 2 }}>{initial}</span>
    </div>
  );
}
